/* assets/littleg-kid-interaction-standard.js — LITTLEG-KID-INTERACTION-STANDARD-30
 * Familiar presentation adapter for /pages/kid-map.
 *
 * RESPONSIBILITY BOUNDARY (0 Conflict / 0 Overlap Contract)
 * - Geographic Map Rendering Owner: LittleGKidMapRenderer from assets/littleg-kid-mapbox-runtime.js
 * - Social Data Persistence Owner: LittleGFamilyData.socialGraph (read-only here)
 * - Social Opening & Marker Owner: LittleGKidMap.openSocialPlace (assets/littleg-kid-map.js)
 * - Friend Card Semantics / CRUD / Distance Owner: assets/littleg-kid-map.js (V2.17.0)
 * - Around-station State & Public POI Handoffs Owner: assets/littleg-kid-handoffs.js (V1.9.0)
 * - Locale Form Owner: #KidMapLocalizationForm
 * - THIS FILE OWNS EXCLUSIVELY:
 *   1. Familiar sheet presentation (Social, Language, Share sheets) & swipe-down dismissal.
 *   2. Language selection modal choreography & #KidMapLocalizationForm bridge.
 *   3. Safe external actions (Directions, Meet up, Say hi, Family Share).
 *   4. SearchEngagement zero-state & quick search shortcuts tray.
 *   5. Station dialog top-layer choreography only (not station data/save).
 *
 * NON-OWNERSHIP GUARANTEE:
 * Never initializes a map, persists social/location data, rewrites Friend Card semantic copy,
 * owns Nearby filters, requests geolocation, infers home location, or creates a second search engine.
 */
(function (window, document) {
  'use strict';

  var VERSION = '1.27.7';
  var ROOT_SELECTOR = '[data-littleg-kid-map]';
  var SAFE_SHARE_PATH = '/pages/littleg-map?source=friend_share&intent=friends';
  var SHEET_DISMISS_PX = 72;
  var lastActiveSheetElement = null;

  if (window.__LITTLEG_KID_INTERACTION_STANDARD__ === VERSION) return;
  window.__LITTLEG_KID_INTERACTION_STANDARD__ = VERSION;

  function q(root, selector) {
    return root ? root.querySelector(selector) : null;
  }
  function qa(root, selector) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(selector)) : [];
  }

  function clean(value, max) {
    return String(value == null ? '' : value)
      .replace(/[\u0000-\u001f\u007f<>"'`]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, max || 180);
  }

  function finite(value) {
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function isChinese() {
    return /^zh\b/i.test(document.documentElement.lang || '');
  }
  function isSimplifiedChinese() {
    return /^zh-(?:cn|hans)\b/i.test(document.documentElement.lang || '');
  }

  function graph() {
    var family = window.LittleGFamilyData;
    var social = family && family.socialGraph;

    return social &&
      typeof social.listPlaces === 'function' &&
      typeof social.getPlace === 'function'
      ? social
      : null;
  }

  function confirmed(row) {
    return (
      !!row &&
      row.privacyConfirmed === true &&
      row.parentConfirmed === true &&
      row.approvalStatus === 'approved'
    );
  }

  function rows() {
    var social = graph();
    var list = social ? social.listPlaces() : [];

    return Array.isArray(list)
      ? list.filter(function (row) {
          return row && row.visibility === 'family_private' && confirmed(row);
        })
      : [];
  }

  function rowById(id) {
    var social = graph();
    var key = clean(id, 120);

    if (!social || !key) return null;

    var row = social.getPlace(key);

    return row && row.visibility === 'family_private' && confirmed(row) ? row : null;
  }

  function stationName(row) {
    if (!row || clean(row.locationType || row.location_type, 40) !== 'station_anchor') return '';
    return clean(
      isChinese() ? row.stationNameZhHk || row.stationName : row.stationName || row.stationNameZhHk,
      120
    );
  }

  function toast(root, message) {
    var runtime = window.LittleGKidMap;
    if (!runtime || typeof runtime.notify !== 'function') return false;
    return runtime.notify(message);
  }

  function safeShareUrl() {
    var path = clean(window.location.pathname, 240);

    if (!path || !/\/pages\/littleg-map\/?$/.test(path)) {
      return window.location.origin + SAFE_SHARE_PATH;
    }

    return window.location.origin + path + '?source=friend_share&intent=friends';
  }

  function promoteStationDialog(root) {
    var host = q(root, '[data-kid-station-modal]');
    if (!host || host.tagName === 'DIALOG') return host;
    var dialog = document.createElement('dialog');
    Array.prototype.slice.call(host.attributes || []).forEach(function (attribute) {
      if (attribute.name === 'role' || attribute.name === 'aria-modal') return;
      dialog.setAttribute(attribute.name, attribute.value);
    });
    dialog.setAttribute('data-kid-station-modal', '');
    dialog.setAttribute('aria-modal', 'true');
    dialog.className = host.className;
    dialog.hidden = host.hidden;
    while (host.firstChild) dialog.appendChild(host.firstChild);
    host.replaceWith(dialog);
    root.setAttribute('data-kid-station-dialog', 'native-top-layer');
    return dialog;
  }

  function flyTo(root, options) {
    var renderer = window.LittleGKidMapRenderer;

    if (!renderer || typeof renderer.flyTo !== 'function' || !options) {
      return false;
    }

    return renderer.flyTo(root, {
      lat: Number(options.lat),
      lng: Number(options.lng),
      zoom: Number(options.zoom) || 15,
    });
  }

  function flyToRow(root, row) {
    if (!row) return false;

    var lat = finite(row.lat);
    var lng = finite(row.lng);

    if (lat === null || lng === null) return false;

    var renderer = window.LittleGKidMapRenderer;
    var zoom =
      renderer && typeof renderer.getZoom === 'function'
        ? Math.max(Number(renderer.getZoom(root)) || 0, 15)
        : 15;

    return flyTo(root, { lat: lat, lng: lng, zoom: zoom });
  }

  function openSocialRow(root, id) {
    var row = rowById(id);
    var opened = false;
    if (window.LittleGKidMap && typeof window.LittleGKidMap.openSocialPlace === 'function') {
      opened = window.LittleGKidMap.openSocialPlace(id);
    }
    window.setTimeout(function () {
      if (row) flyToRow(root, row);
      syncFriendActions(root);
    }, 0);
    return opened;
  }

  function focusableInSheet(sheet) {
    return q(
      sheet,
      '[data-kid-language-sheet-close], button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function visibleFocusTarget(node) {
    return Boolean(
      node &&
      node.isConnected &&
      typeof node.focus === 'function' &&
      (typeof node.getClientRects !== 'function' || node.getClientRects().length > 0)
    );
  }

  function resolvedSheetOpener(root, opener, fallbackSelector) {
    if (visibleFocusTarget(opener)) return opener;
    var fallback = fallbackSelector ? q(root, fallbackSelector) : null;
    if (visibleFocusTarget(fallback)) return fallback;
    var search = q(root, '[data-gproperty-map-search-input]');
    if (visibleFocusTarget(search)) return search;
    var language = q(root, '[data-kid-map-language]');
    if (visibleFocusTarget(language)) return language;
    return null;
  }

  function syncLanguageSheetReserve(sheet, open) {
    if (!sheet || !sheet.hasAttribute || !sheet.hasAttribute('data-kid-language-sheet')) return;
    var host = sheet.closest(ROOT_SELECTOR);
    if (!host) return;

    if (!open) {
      host.removeAttribute('data-kid-language-sheet-open');
      host.style.removeProperty('--kid-language-sheet-reserved-bottom');
      return;
    }

    host.setAttribute('data-kid-language-sheet-open', 'true');
    var rect = sheet.getBoundingClientRect();
    var reserved = Math.max(0, Math.ceil(window.innerHeight - rect.top));
    host.style.setProperty('--kid-language-sheet-reserved-bottom', reserved + 'px');
  }

  function showSheet(sheet, opener) {
    if (!sheet) return;
    lastActiveSheetElement =
      opener ||
      (document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : null);
    sheet.__kidGmapsOpener = lastActiveSheetElement;

    if (
      sheet.__kidGmapsOpener &&
      sheet.__kidGmapsOpener.hasAttribute &&
      sheet.__kidGmapsOpener.hasAttribute('aria-expanded')
    ) {
      sheet.__kidGmapsOpener.setAttribute('aria-expanded', 'true');
    }

    sheet.hidden = false;
    var heading = q(sheet, 'h2, h3');
    if (heading && !heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');

    window.requestAnimationFrame(function () {
      syncLanguageSheetReserve(sheet, true);
      var target = heading || focusableInSheet(sheet) || sheet;
      if (target && typeof target.focus === 'function') {
        try {
          target.focus({ preventScroll: true });
        } catch (_) {
          target.focus();
        }
      }
    });
  }

  function restoreSheetFocus(host, preferred) {
    var target = resolvedSheetOpener(host, preferred, null);
    if (!target || typeof target.focus !== 'function') return null;

    function apply() {
      var resolvedTarget = resolvedSheetOpener(host, target, null);
      if (!resolvedTarget || typeof resolvedTarget.focus !== 'function') return null;
      if (document.activeElement === resolvedTarget) return resolvedTarget;

      try {
        resolvedTarget.focus({ preventScroll: true });
      } catch (_) {
        resolvedTarget.focus();
      }
      return resolvedTarget;
    }

    apply();
    window.requestAnimationFrame(function () {
      var active = document.activeElement;
      if (!active || active === document.body || !visibleFocusTarget(active)) apply();
    });
    return target;
  }

  function hideSheet(sheet, options) {
    if (!sheet) return;
    var restoreFocus = !options || options.restoreFocus !== false;
    var opener = sheet.__kidGmapsOpener || lastActiveSheetElement;
    var host = sheet.closest(ROOT_SELECTOR);
    var focusTarget = restoreFocus ? resolvedSheetOpener(host, opener, null) : null;

    sheet.hidden = true;
    sheet.style.removeProperty('transform');
    syncLanguageSheetReserve(sheet, false);

    if (opener && opener.hasAttribute && opener.hasAttribute('aria-expanded')) {
      opener.setAttribute('aria-expanded', 'false');
    }

    if (!restoreFocus) return;
    restoreSheetFocus(host, focusTarget);
  }

  function closeSheet(sheet, options) {
    if (!sheet) return;
    var stationHost =
      sheet.matches && sheet.matches('.kid-station-modal__card')
        ? sheet.closest('[data-kid-station-modal]')
        : null;
    if (stationHost) {
      var stationClose = q(stationHost, '[data-kid-station-close]');
      if (stationClose) stationClose.click();
      else {
        if (
          stationHost.tagName === 'DIALOG' &&
          stationHost.open &&
          typeof stationHost.close === 'function'
        ) {
          try {
            stationHost.close();
          } catch (_) {}
        }
        stationHost.hidden = true;
      }
      sheet.style.removeProperty('transform');
      sheet.style.removeProperty('--kid-friend-swipe-y');
      return;
    }

    if (sheet.tagName === 'DIALOG') {
      if (sheet.open && typeof sheet.close === 'function') {
        try {
          sheet.close();
        } catch (_) {
          sheet.removeAttribute('open');
        }
      } else {
        sheet.removeAttribute('open');
      }
      sheet.hidden = false;
      sheet.removeAttribute('hidden');
      sheet.style.removeProperty('transform');
      sheet.style.removeProperty('--kid-friend-swipe-y');
    } else {
      hideSheet(sheet, options);
    }
  }

  function closeExistingCard(root, selector, closeSelector) {
    var card = q(root, selector);
    if (!card || card.hidden) return;
    var close = closeSelector ? q(card, closeSelector) : null;
    if (close) close.click();
    else closeSheet(card);
  }

  function closeTransientSheets(root, except) {
    if (!(root instanceof HTMLElement)) return;
    qa(root, '[data-kid-gmaps-sheet]').forEach(function (sheet) {
      if (sheet === except) return;
      if (sheet.matches('[data-kid-friend-card]')) return;
      if (sheet.matches('[data-gproperty-map-selection]')) return;
      closeSheet(sheet, { restoreFocus: false });
    });

    closeExistingCard(root, '[data-kid-friend-card]', '[data-kid-friend-card-close]');

    var place = q(root, '[data-gproperty-map-selection]');
    if (place && place !== except) {
      place.hidden = true;
      place.setAttribute('data-sheet-state', 'collapsed');
      place.style.removeProperty('transform');
    }
  }

  function addSheetHandle(sheet, label) {
    if (!sheet || q(sheet, '[data-kid-gmaps-sheet-handle], [data-gproperty-map-sheet-toggle]'))
      return null;
    var handle = document.createElement('button');
    handle.type = 'button';
    handle.className = 'kid-gmaps-sheet__handle';
    handle.setAttribute('data-kid-gmaps-sheet-handle', '');
    handle.setAttribute(
      'aria-label',
      label || (isChinese() ? '向下滑動關閉' : 'Swipe down to close')
    );
    handle.innerHTML = '<span aria-hidden="true"></span>';
    sheet.insertBefore(handle, sheet.firstChild);
    return handle;
  }

  function bindSwipeDismiss(sheet, closeFn) {
    if (!sheet || sheet.__kidGmapsSwipeBound) return;
    var handle = q(sheet, '[data-kid-gmaps-sheet-handle]');
    if (!handle) return;

    sheet.__kidGmapsSwipeBound = true;
    var startY = 0;
    var deltaY = 0;
    var pointerId = null;
    var suppressClick = false;

    handle.addEventListener('pointerdown', function (event) {
      pointerId = event.pointerId;
      startY = event.clientY;
      deltaY = 0;
      suppressClick = false;
      sheet.classList.add('is-dragging');
      try {
        handle.setPointerCapture(pointerId);
      } catch (_) {}
    });

    handle.addEventListener('pointermove', function (event) {
      if (pointerId === null || event.pointerId !== pointerId) return;
      deltaY = Math.max(0, event.clientY - startY);
      if (deltaY > 6) suppressClick = true;
      if (sheet.matches('[data-kid-friend-card]')) {
        sheet.style.setProperty('--kid-friend-swipe-y', Math.min(deltaY, 180) + 'px');
      } else {
        sheet.style.transform = 'translate3d(0,' + Math.min(deltaY, 180) + 'px,0)';
      }
    });

    function finish(event) {
      if (pointerId === null || (event && event.pointerId !== pointerId)) return;
      var shouldClose = deltaY >= SHEET_DISMISS_PX;
      pointerId = null;
      sheet.classList.remove('is-dragging');
      sheet.style.removeProperty('transform');
      sheet.style.removeProperty('--kid-friend-swipe-y');
      if (shouldClose && typeof closeFn === 'function') closeFn();
    }

    handle.addEventListener('pointerup', finish);
    handle.addEventListener('pointercancel', finish);
    handle.addEventListener('click', function (event) {
      if (suppressClick) {
        suppressClick = false;
        event.preventDefault();
        return;
      }
      if (typeof closeFn === 'function') closeFn();
    });
  }

  function prepareSheet(sheet, closeFn) {
    if (!sheet || sheet.tagName === 'DIALOG') return;
    sheet.setAttribute('data-kid-gmaps-sheet', '');
    addSheetHandle(sheet);
    var close =
      closeFn ||
      function () {
        closeSheet(sheet);
      };
    bindSwipeDismiss(sheet, close);
  }

  function normalizeNativeDialog(dialog) {
    if (!dialog || dialog.tagName !== 'DIALOG') return;
    dialog.removeAttribute('data-kid-gmaps-sheet');
    dialog.hidden = false;
    dialog.removeAttribute('hidden');
    var handle = q(dialog, '[data-kid-gmaps-sheet-handle]');
    if (handle) handle.remove();
    if (!dialog.open) dialog.removeAttribute('open');
  }

  function prepareExistingSheets(root) {
    var friend = q(root, '[data-kid-friend-card]');
    var place = q(root, '[data-gproperty-map-selection]');
    var add = q(root, '[data-kid-map-add-modal]');
    var consent = q(root, '.kid-location-consent-modal');
    var stationHost = promoteStationDialog(root);
    var stationCard = q(stationHost, '.kid-station-modal__card');

    normalizeNativeDialog(add);
    normalizeNativeDialog(consent);

    prepareSheet(friend, function () {
      var close = q(friend, '[data-kid-friend-card-close]');
      if (close) close.click();
      else closeSheet(friend);
    });
    prepareSheet(place, function () {
      place.hidden = true;
      place.setAttribute('data-sheet-state', 'collapsed');
    });
    prepareSheet(stationCard, function () {
      var close = q(stationHost, '[data-kid-station-close]');
      if (close) close.click();
      else closeSheet(stationCard);
    });
  }

  function sheetHeader(title, closeHook) {
    const closeIcon =
      (window.LittleGControlIcons && window.LittleGControlIcons.close) ||
      '<span aria-hidden="true">×</span>';

    return (
      '<div class="kid-add-modal__head"><h2>' +
      escapeHtml(title) +
      '</h2>' +
      '<button type="button" aria-label="' +
      (isChinese() ? '關閉' : 'Close') +
      '" ' +
      closeHook +
      '>' +
      closeIcon +
      '</button></div>'
    );
  }

  function socialRows(kind) {
    return rows().filter(function (row) {
      var type = clean(row.type || row.kind, 20) === 'mine' ? 'mine' : 'friend';
      return !kind || type === kind;
    });
  }

  function ensureSocialSheet(root) {
    var existing = q(root, '[data-kid-social-sheet]');
    if (existing) return existing;

    var sheet = document.createElement('section');
    sheet.id = 'KidGmapsSocialSheet';
    sheet.className = 'kid-gmaps-sheet kid-gmaps-social-sheet';
    sheet.setAttribute('data-kid-gmaps-sheet', '');
    sheet.setAttribute('data-kid-social-sheet', '');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-labelledby', 'KidGmapsSocialSheetTitle');
    sheet.tabIndex = -1;
    sheet.hidden = true;
    sheet.innerHTML =
      '<div class="kid-gmaps-sheet__body">' +
      sheetHeader('', 'data-kid-language-sheet-close') +
      '<p data-kid-social-sheet-subtitle></p><div class="kid-gmaps-social-list" data-kid-social-sheet-list></div></div>';

    q(sheet, 'h2').id = 'KidGmapsSocialSheetTitle';
    q(sheet, 'h2').setAttribute('data-kid-social-sheet-title', '');
    root.appendChild(sheet);

    prepareSheet(sheet, function () {
      hideSheet(sheet);
    });
    q(sheet, '[data-kid-language-sheet-close]').addEventListener('click', function () {
      hideSheet(sheet);
    });
    q(sheet, '[data-kid-social-sheet-list]').addEventListener('click', function (event) {
      var button = event.target.closest('[data-kid-social-sheet-row]');
      if (!button) return;
      hideSheet(sheet, { restoreFocus: false });
      openSocialRow(root, button.getAttribute('data-kid-social-sheet-row'));
    });
    return sheet;
  }

  function renderSocialSheet(root, kind, trigger, options) {
    var sheet = ensureSocialSheet(root);
    var title = q(sheet, '[data-kid-social-sheet-title]');
    var subtitle = q(sheet, '[data-kid-social-sheet-subtitle]');
    var list = q(sheet, '[data-kid-social-sheet-list]');
    var stationId = clean(options && options.stationId, 120);

    var items = socialRows(kind);

    if (stationId) {
      items = items.filter(function (row) {
        return clean(row && (row.stationId || row.station_id), 120) === stationId;
      });
    }

    sheet.__kidStationFilterId = stationId;

    if (stationId) {
      sheet.setAttribute('data-kid-social-sheet-mode', 'station');
      sheet.setAttribute('data-kid-social-sheet-count', String(items.length));
    } else {
      sheet.removeAttribute('data-kid-social-sheet-mode');
      sheet.removeAttribute('data-kid-social-sheet-count');
    }

    var selectedSocialId = clean(root.getAttribute('data-kid-selected-social-id'), 120);

    if (stationId) {
      const stationLabel = items.length ? stationName(items[0]) : '';
      title.textContent = stationLabel || (isChinese() ? '同站朋友' : 'Friends at this station');
      subtitle.textContent = isChinese()
        ? items.length + ' 個朋友使用這個港鐵站作為私人位置錨點。'
        : items.length + ' friends use this MTR station as their private location anchor.';
    } else {
      title.textContent =
        kind === 'mine'
          ? isChinese()
            ? '我的地點'
            : 'My places'
          : isChinese()
            ? '朋友'
            : 'Friends';
      subtitle.textContent =
        kind === 'mine'
          ? isChinese()
            ? '你儲存在私人地圖上的地點。'
            : 'Your saved places, all in one private map.'
          : isChinese()
            ? '你的 LittleG 朋友，都在這裡。'
            : 'Your LittleG friends, all in one place.';
    }

    list.replaceChildren();
    items.forEach(function (row) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'kid-gmaps-social-row';
      button.setAttribute('data-kid-social-sheet-row', clean(row.id, 120));
      if (selectedSocialId && clean(row.id, 120) === selectedSocialId) {
        button.setAttribute('data-kid-social-selected', 'true');
        button.setAttribute('aria-current', 'true');
      }
      var icon =
        kind === 'mine'
          ? '🏠'
          : row.relationship === 'classmate'
            ? '🏫'
            : row.relationship === 'family'
              ? '🏡'
              : '👫';
      var station = stationName(row);
      button.innerHTML =
        '<span class="kid-gmaps-social-row__avatar" aria-hidden="true">' +
        icon +
        '</span><span><strong></strong><small></small></span>';
      q(button, 'strong').textContent =
        clean(row.label, 80) ||
        (kind === 'mine'
          ? isChinese()
            ? '我的地點'
            : 'My place'
          : isChinese()
            ? '朋友'
            : 'Friend');
      q(button, 'small').textContent = station
        ? '🚉 ' + station
        : isChinese()
          ? '🚉 尚未選擇車站'
          : '🚉 Station not set';
      list.appendChild(button);
    });

    if (!items.length) {
      var empty = document.createElement('p');
      empty.className = 'kid-gmaps-sheet__empty';
      empty.textContent =
        kind === 'mine'
          ? isChinese()
            ? '還沒有「我的地點」。先加入一個地點。'
            : 'No My places yet. Add your first place.'
          : isChinese()
            ? '還沒有朋友。按「新增朋友」開始。'
            : 'No friends yet. Add your first friend.';

      var emptyVisual = document.createElement('span');
      emptyVisual.className = 'kid-gmaps-empty-visual';
      emptyVisual.setAttribute('aria-hidden', 'true');
      emptyVisual.textContent = kind === 'mine' ? '🏠' : '🗺️';
      empty.prepend(emptyVisual);
      list.appendChild(empty);

      var emptyAction = document.createElement('button');
      emptyAction.type = 'button';
      emptyAction.className = 'kid-map-action kid-map-action--primary';
      emptyAction.textContent =
        kind === 'mine'
          ? isChinese()
            ? '＋ 我的地點'
            : '+ My place'
          : isChinese()
            ? '＋ 新增朋友'
            : '+ Friend';

      emptyAction.addEventListener('click', function () {
        hideSheet(sheet, { restoreFocus: false });
        var target =
          kind === 'mine'
            ? q(root, '[data-kid-empty-add="mine"]')
            : q(root, '[data-kid-add-kind="friend"]');
        if (target) target.click();
      });

      list.appendChild(emptyAction);
    }

    closeTransientSheets(root, sheet);
    showSheet(sheet, resolvedSheetOpener(root, trigger, '[data-gproperty-map-search-input]'));
  }

  function bindStationFriendsSheet(root) {
    if (!root || root.__kidStationFriendsSheetBound) return;
    root.__kidStationFriendsSheetBound = true;

    root.addEventListener('littleg:kid-map:station-friends', function (event) {
      var stationId = clean(event && event.detail && event.detail.stationId, 120);
      if (!stationId) return;

      renderSocialSheet(root, 'friend', null, { stationId: stationId });
    });
  }

  function decorateCoreFilters(root) {
    var friends = q(root, '[data-kid-filter="friends"]');
    var mine = q(root, '[data-kid-filter="mine"]');

    if (friends && !friends.__kidGmapsListBound) {
      friends.__kidGmapsListBound = true;
      friends.setAttribute('aria-controls', 'KidGmapsSocialSheet');
      friends.setAttribute('aria-expanded', 'false');
      friends.addEventListener('click', function () {
        window.setTimeout(function () {
          renderSocialSheet(root, 'friend', friends);
        }, 0);
      });
    }

    if (mine && !mine.__kidGmapsListBound) {
      mine.__kidGmapsListBound = true;
      mine.setAttribute('aria-controls', 'KidGmapsSocialSheet');
      mine.setAttribute('aria-expanded', 'false');
      mine.addEventListener('click', function () {
        window.setTimeout(function () {
          renderSocialSheet(root, 'mine', mine);
        }, 0);
      });
    }
  }

  function languageKind(code) {
    var value = clean(code, 30).toLowerCase();
    if (/^en(?:-|$)/.test(value)) return 'en';
    if (/^zh-(?:cn|hans|sg)$/.test(value)) return 'hans';
    if (/^zh-(?:hk|tw|hant)$/.test(value)) return 'hant';
    if (value === 'zh') return 'hant';
    return '';
  }

  function currentLanguageKind() {
    var code = clean(document.documentElement.lang, 30).toLowerCase();
    return languageKind(code) || (isChinese() ? 'hant' : 'en');
  }

  function languageLongLabel(kind) {
    if (kind === 'hans') return '简体中文';
    if (kind === 'hant') return '繁體中文';
    return 'English';
  }

  function languageShortLabel(kind) {
    if (kind === 'hans') return '简';
    if (kind === 'hant') return '繁';
    return 'EN';
  }

  function languageOptions(form) {
    var seen = new Set();
    var out = [];
    qa(form, 'input[name="language_code"]').forEach(function (input) {
      var kind = languageKind(input.value);
      if (!kind || seen.has(kind)) return;
      seen.add(kind);
      out.push({ kind: kind, input: input, label: languageLongLabel(kind) });
    });
    return out.sort(function (a, b) {
      var order = { hant: 0, hans: 1, en: 2 };
      return order[a.kind] - order[b.kind];
    });
  }

  function syncLanguageTrigger(root) {
    var button = q(root, '[data-kid-map-language]');
    if (!button) return;
    var label = q(button, '[data-kid-language-label]');
    if (!label) {
      label = document.createElement('span');
      label.className = 'visually-hidden';
      label.setAttribute('data-kid-language-label', '');
      button.appendChild(label);
    }
    var kind = currentLanguageKind();
    label.textContent = languageShortLabel(kind);
    button.setAttribute('data-kid-language-current', kind);
    button.setAttribute('data-kid-language-label-ready', 'true');
    button.setAttribute('aria-label', isChinese() ? '選擇語言' : 'Choose language');
  }

  function setLanguageOptionState(sheet, activeButton) {
    qa(sheet, '[data-kid-language-option]').forEach(function (candidate) {
      candidate.setAttribute('aria-checked', candidate === activeButton ? 'true' : 'false');
    });
  }

  function submitLanguageOption(sheet, form, option, button) {
    if (!sheet || !form || !option || !option.input || !button) return false;

    setLanguageOptionState(sheet, button);

    if (option.kind === currentLanguageKind()) {
      hideSheet(sheet);
      return true;
    }

    option.input.checked = true;
    option.input.dispatchEvent(new Event('input', { bubbles: true }));
    option.input.dispatchEvent(new Event('change', { bubbles: true }));

    var returnTo = q(form, 'input[name="return_to"]');
    if (returnTo) returnTo.value = window.location.pathname + window.location.search;

    sheet.setAttribute('aria-busy', 'true');
    button.setAttribute('data-kid-language-pending', 'true');

    window.setTimeout(function () {
      if (!document.contains(sheet)) return;
      sheet.removeAttribute('aria-busy');
      button.removeAttribute('data-kid-language-pending');
    }, 2000);

    var nativeSubmit = window.HTMLFormElement && window.HTMLFormElement.prototype.submit;
    if (typeof nativeSubmit === 'function') {
      nativeSubmit.call(form);
      return true;
    }

    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
    return true;
  }

  function ensureLanguageSheet(root, form) {
    var existing = q(root, '[data-kid-language-sheet]');
    if (existing) return existing;

    var sheet = document.createElement('section');
    sheet.id = 'KidGmapsLanguageSheet';
    sheet.className = 'kid-gmaps-sheet kid-gmaps-language-sheet';
    sheet.setAttribute('data-kid-gmaps-sheet', '');
    sheet.setAttribute('data-kid-language-sheet', '');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-labelledby', 'KidGmapsLanguageSheetTitle');
    sheet.tabIndex = -1;
    sheet.hidden = true;
    sheet.innerHTML =
      '<div class="kid-gmaps-sheet__body">' +
      sheetHeader('🌐 ' + (isChinese() ? '語言' : 'Language'), 'data-kid-language-sheet-close') +
      '<p>' +
      (isChinese() ? '選擇 LittleG 顯示語言。' : 'Choose your preferred language for LittleG.') +
      '</p>' +
      '<div class="kid-gmaps-language-list" data-kid-language-list></div></div>';

    q(sheet, 'h2').id = 'KidGmapsLanguageSheetTitle';
    root.appendChild(sheet);

    prepareSheet(sheet, function () {
      hideSheet(sheet);
    });
    q(sheet, '[data-kid-language-sheet-close]').addEventListener('click', function () {
      hideSheet(sheet);
    });

    var list = q(sheet, '[data-kid-language-list]');
    languageOptions(form).forEach(function (option) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'kid-gmaps-language-option';
      button.setAttribute('data-kid-language-option', option.kind);
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', option.kind === currentLanguageKind() ? 'true' : 'false');
      button.innerHTML =
        '<span class="kid-gmaps-language-option__radio" aria-hidden="true"></span><span></span>';
      button.lastElementChild.textContent = option.label;

      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        submitLanguageOption(sheet, form, option, button);
      });
      list.appendChild(button);
    });
    return sheet;
  }

  function setupLanguageSheet(root) {
    var button = q(root, '[data-kid-map-language]');
    if (!button) return;
    syncLanguageTrigger(root);
    var form = document.getElementById('KidMapLocalizationForm');
    if (!form || button.__kidLanguageSheetBound) return;

    var sheet = ensureLanguageSheet(root, form);
    button.__kidLanguageSheetBound = true;
    button.removeAttribute('data-global-localization-trigger');
    button.setAttribute('data-kid-language-sheet-trigger', '');
    button.setAttribute('aria-controls', sheet.id);
    button.setAttribute('aria-expanded', 'false');

    button.addEventListener(
      'click',
      function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeTransientSheets(root, sheet);
        showSheet(sheet, resolvedSheetOpener(root, button, '[data-kid-map-language]'));
        qa(sheet, '[data-kid-language-option]').forEach(function (option) {
          option.setAttribute(
            'aria-checked',
            option.getAttribute('data-kid-language-option') === currentLanguageKind()
              ? 'true'
              : 'false'
          );
        });
      },
      true
    );
  }

  function memoryViewFocusTarget(sheet) {
    if (!sheet) return null;
    return (
      q(sheet, '[data-kid-memory-view-option][aria-pressed="true"]:not(:disabled)') ||
      q(sheet, '[data-kid-memory-view-option]:not(:disabled)')
    );
  }

  function focusMemoryViewOption(sheet) {
    var target = memoryViewFocusTarget(sheet);
    if (!target) return;

    qa(sheet, '[data-kid-memory-view-option]').forEach(function (node) {
      if (node === target) node.setAttribute('autofocus', '');
      else node.removeAttribute('autofocus');
    });

    if (!visibleFocusTarget(target)) return;
    try {
      target.focus({ preventScroll: true });
    } catch (_) {
      target.focus();
    }
  }

  function memoryViewTriggerAriaLabel(label) {
    var value = clean(label, 80);
    var kind = currentLanguageKind();

    if (kind === 'hans') {
      return '当前：' + value + '，点击选择其他地图';
    }

    if (kind === 'hant') {
      return '目前：' + value + '，按一下選擇其他地圖';
    }

    return 'Current: ' + value + '. Choose another map.';
  }
  function restoreMemoryViewFocus(root) {
    if (!(root instanceof HTMLElement)) return;
    var trigger = q(root, '[data-kid-memory-view-open]');
    var target = root.__kidMemoryViewOpener;
    root.__kidMemoryViewOpener = null;

    if (!visibleFocusTarget(target)) target = trigger;
    if (!visibleFocusTarget(target)) return;

    window.requestAnimationFrame(function () {
      if (!visibleFocusTarget(target)) return;
      try {
        target.focus({ preventScroll: true });
      } catch (_) {
        target.focus();
      }
    });
  }

  function setMemoryViewSheetOpen(root, open, opener) {
    if (!(root instanceof HTMLElement)) return;
    var trigger = q(root, '[data-kid-memory-view-open]');
    var sheet = q(root, '[data-kid-memory-view-sheet]');
    if (!trigger || !sheet) return;

    if (open) {
      root.__kidMemoryViewOpener = visibleFocusTarget(opener) ? opener : trigger;
      closeTransientSheets(root, sheet);
      trigger.setAttribute('aria-expanded', 'true');

      var target = memoryViewFocusTarget(sheet);
      qa(sheet, '[data-kid-memory-view-option]').forEach(function (node) {
        if (node === target) node.setAttribute('autofocus', '');
        else node.removeAttribute('autofocus');
      });

      if (!sheet.open) {
        try {
          if (typeof sheet.showModal === 'function') sheet.showModal();
          else sheet.setAttribute('open', '');
        } catch (_) {
          sheet.setAttribute('open', '');
        }
      }

      window.requestAnimationFrame(function () {
        if (!sheet.open || sheet.contains(document.activeElement)) return;
        focusMemoryViewOption(sheet);
      });
      return;
    }

    trigger.setAttribute('aria-expanded', 'false');

    if (sheet.open && typeof sheet.close === 'function') {
      try {
        sheet.close();
      } catch (_) {
        sheet.removeAttribute('open');
        restoreMemoryViewFocus(root);
      }
    } else {
      sheet.removeAttribute('open');
      restoreMemoryViewFocus(root);
    }
  }

  function bindMemoryViewSelector(root) {
    if (!root || root.__kidMemoryViewBound) return;
    var trigger = q(root, '[data-kid-memory-view-open]');
    var sheet = q(root, '[data-kid-memory-view-sheet]');
    var close = q(root, '[data-kid-memory-view-close]');
    var label = q(root, '[data-kid-memory-view-label]');
    var icon = q(root, '[data-kid-memory-view-icon]');
    if (!trigger || !sheet || !close || !label || !icon) return;
    root.__kidMemoryViewBound = true;
    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      setMemoryViewSheetOpen(root, true, trigger);
    });
    close.addEventListener('click', function () {
      setMemoryViewSheetOpen(root, false);
    });
    sheet.addEventListener('close', function () {
      trigger.setAttribute('aria-expanded', 'false');
      restoreMemoryViewFocus(root);
    });
    sheet.addEventListener('focusout', function () {
      window.requestAnimationFrame(function () {
        if (!sheet.open || sheet.contains(document.activeElement)) return;
        focusMemoryViewOption(sheet);
      });
    });
    sheet.addEventListener('cancel', function (event) {
      event.preventDefault();
      setMemoryViewSheetOpen(root, false);
    });
    sheet.addEventListener('click', function (event) {
      if (event.target === sheet) {
        setMemoryViewSheetOpen(root, false);
        return;
      }
      var option = event.target.closest('[data-kid-memory-view-option]');
      if (!option || option.disabled || option.getAttribute('aria-disabled') === 'true') return;
      var next = clean(option.getAttribute('data-kid-memory-view-option'), 32) || 'friends';
      root.setAttribute('data-kid-memory-view', next);
      qa(sheet, '[data-kid-memory-view-option]').forEach(function (node) {
        var selected = node === option;
        node.setAttribute('aria-pressed', selected ? 'true' : 'false');
        var state = q(node, '.kid-memory-view-option__state');
        if (state) state.textContent = selected ? '✓' : '';
      });
      try {
        root.dispatchEvent(
          new CustomEvent('littleg:kid-map:memory-view-change', {
            bubbles: true,
            detail: { view: next },
          })
        );
      } catch (_) {}
      var strong = q(option, 'strong');
      var nextIcon = clean(option.getAttribute('data-memory-view-icon'), 8);
      var nextLabel = strong ? clean(strong.textContent, 60) : clean(label.textContent, 60);
      if (nextLabel) label.textContent = nextLabel;
      if (nextIcon) icon.textContent = nextIcon;
      trigger.setAttribute('aria-label', memoryViewTriggerAriaLabel(nextLabel));
      setMemoryViewSheetOpen(root, false);
    });
  }

  function bindNightModePreference(root) {
    if (!root || root.__kidNightModeBound) return;
    root.__kidNightModeBound = true;

    var media =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;

    function sync() {
      var enabled = !!(media && media.matches);
      root.setAttribute('data-kid-night-mode', enabled ? 'on' : 'off');

      var renderer = window.LittleGKidMapRenderer;
      if (renderer && typeof renderer.setNightMode === 'function') {
        renderer.setNightMode(root, enabled);
      }
    }

    if (media) {
      if (typeof media.addEventListener === 'function') {
        media.addEventListener('change', sync);
      } else if (typeof media.addListener === 'function') {
        media.addListener(sync);
      }
    }

    sync();
  }

  function bindMotionPreference(root) {
    if (!root || root.__kidMotionPreferenceBound) return;
    root.__kidMotionPreferenceBound = true;

    var media =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;

    function sync() {
      root.setAttribute('data-kid-motion-mode', media && media.matches ? 'reduced' : 'full');
    }

    if (media) {
      if (typeof media.addEventListener === 'function') {
        media.addEventListener('change', sync);
      } else if (typeof media.addListener === 'function') {
        media.addListener(sync);
      }
    }

    sync();
  }

  function ensureOfflineBanner(root) {
    var banner = q(root, '[data-kid-offline-banner]');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'kid-gmaps-offline-bar';
      banner.setAttribute('data-kid-offline-banner', '');
      banner.setAttribute('role', 'status');
      banner.setAttribute('aria-live', 'polite');
      banner.textContent = isChinese()
        ? '📶 網路休息中，展示已儲存的車站'
        : '📶 Offline · Showing saved stations';
      root.appendChild(banner);
    }

    function sync() {
      var online = navigator.onLine !== false;
      root.setAttribute('data-kid-online', online ? 'true' : 'false');
      banner.hidden = online;
    }

    if (!root.__kidOnlineStateBound) {
      root.__kidOnlineStateBound = true;
      window.addEventListener('online', sync);
      window.addEventListener('offline', sync);
    }
    sync();
  }

  function isFriendShareEntry() {
    try {
      var params = new URLSearchParams(window.location.search);
      return clean(params.get('source'), 40) === 'friend_share';
    } catch (_) {
      return false;
    }
  }

  function syncOpeningState(root) {
    if (!(root instanceof HTMLElement)) return;

    /*
     * Opening lifecycle is owned by:
     * assets/littleg-kid-opening.js
     *
     * This presentation adapter owns only
     * onboarding/invite projection consumed
     * by the Opening component.
     */
    root.setAttribute('data-kid-onboarding-state', rows().length ? 'returning' : 'first-use');

    root.setAttribute('data-kid-invite-active', isFriendShareEntry() ? 'true' : 'false');
  }

  function bindOpeningState(root) {
    if (!root || root.__kidOpeningBound) return;
    root.__kidOpeningBound = true;
    syncOpeningState(root);
    document.addEventListener('littleg:family-social-graph-change', function () {
      syncOpeningState(root);
    });
  }

  function mapsDirectionsUrl(row) {
    var station = stationName(row);
    if (!station) return '';
    return (
      'https://www.google.com/maps/dir/?api=1&destination=' +
      encodeURIComponent(station + ' MTR Station Hong Kong')
    );
  }

  function currentSelectedRow(root) {
    return rowById(
      root && (root.getAttribute('data-kid-selected-social-id') || root.__kidOpenSocialId)
    );
  }

  function setFriendActionsExpanded(root, expanded) {
    var card = q(root, '[data-kid-friend-card]');
    var toggle = q(card, '[data-kid-friend-actions-toggle]');
    var more = q(card, '[data-kid-friend-more]');

    if (!card || !toggle || !more) return;

    var open = expanded === true;

    card.setAttribute('data-kid-friend-actions-expanded', open ? 'true' : 'false');

    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.setAttribute(
      'aria-label',
      open
        ? isChinese()
          ? '收起更多操作'
          : 'Hide more actions'
        : isChinese()
          ? '更多操作'
          : 'More actions'
    );

    more.hidden = !open;

    var glyph = q(toggle, '.kid-friend-card__action-icon');
    if (glyph) glyph.textContent = open ? '×' : '•••';
  }

  function bindFriendActionDisclosure(root) {
    var card = q(root, '[data-kid-friend-card]');
    var toggle = q(card, '[data-kid-friend-actions-toggle]');
    if (!card || !toggle || toggle.__kidGmapsDisclosureBound) return;

    toggle.__kidGmapsDisclosureBound = true;
    toggle.addEventListener('click', function () {
      setFriendActionsExpanded(
        root,
        card.getAttribute('data-kid-friend-actions-expanded') !== 'true'
      );
    });

    var close = q(card, '[data-kid-friend-card-close]');
    if (close && !close.__kidGmapsDisclosureResetBound) {
      close.__kidGmapsDisclosureResetBound = true;
      close.addEventListener('click', function () {
        setFriendActionsExpanded(root, false);
      });
    }
    setFriendActionsExpanded(root, false);
  }

  function ensureFriendActions(root) {
    var card = q(root, '[data-kid-friend-card]');
    if (!card) return;

    bindFriendActionDisclosure(root);

    if (!card.__kidFriendActionsBound) {
      card.__kidFriendActionsBound = true;

      card.addEventListener('click', function (event) {
        var meetup = event.target.closest('[data-kid-meetup]');
        var hello = event.target.closest('[data-kid-say-hi]');
        var share = event.target.closest('[data-kid-family-share]');

        if (meetup) {
          openShareSheet(root, {
            mode: 'meetup',
            trigger: meetup,
          });
        }

        if (hello) {
          openShareSheet(root, {
            mode: 'hello',
            trigger: hello,
          });
        }

        if (share) {
          openShareSheet(root, {
            mode: 'family',
            trigger: share,
          });
        }
      });
    }

    syncFriendActions(root);
  }

  function syncFriendActions(root) {
    var row = currentSelectedRow(root);
    var station = stationName(row);
    var isFriend = row && clean(row.type || row.kind, 20) === 'friend';

    var directions = q(root, '[data-kid-gmaps-directions]');

    if (directions) {
      var href = mapsDirectionsUrl(row);
      directions.setAttribute('aria-disabled', href ? 'false' : 'true');

      if (href) {
        directions.href = href;
        directions.removeAttribute('tabindex');
      } else {
        directions.removeAttribute('href');
        directions.setAttribute('tabindex', '-1');
      }
    }

    var nearby = q(root, '[data-kid-nearby-open]');
    if (nearby) nearby.hidden = !isFriend || !station;

    var distance = q(root, '[data-kid-friend-distance]');
    if (distance) distance.hidden = !isFriend || !station;

    var meetup = q(root, '[data-kid-meetup]');
    if (meetup) meetup.hidden = !isFriend || !station;

    var hello = q(root, '[data-kid-say-hi]');
    if (hello) hello.hidden = !isFriend || !station;
  }

  function copyText(textVal, root, successMessage) {
    var value = String(textVal || '');
    var failureMessage = isChinese()
      ? '未能複製，請使用瀏覽器分享功能。'
      : 'Could not copy. Use your browser share menu.';

    function legacyCopy() {
      var field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      field.style.pointerEvents = 'none';
      document.body.appendChild(field);
      field.select();
      var ok = false;
      try {
        ok = document.execCommand && document.execCommand('copy');
      } catch (_) {
        ok = false;
      }
      field.remove();
      toast(root, ok ? successMessage : failureMessage);
      return ok;
    }

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      return navigator.clipboard
        .writeText(value)
        .then(function () {
          toast(root, successMessage);
          return true;
        })
        .catch(function () {
          return legacyCopy();
        });
    }
    return Promise.resolve(legacyCopy());
  }

  function sharePayload(payload, fallbackText, root) {
    if (navigator.share) {
      return navigator.share(payload).catch(function (error) {
        if (error && error.name === 'AbortError') return false;
        return copyText(
          fallbackText || payload.text || payload.url || '',
          root,
          isChinese() ? '已複製，可以貼到訊息。' : 'Copied. Paste it into a message.'
        );
      });
    }
    return copyText(
      fallbackText || payload.text || payload.url || '',
      root,
      isChinese() ? '已複製，可以貼到訊息。' : 'Copied. Paste it into a message.'
    );
  }

  function shareMessage(root, mode) {
    var row = currentSelectedRow(root);
    var station = stationName(row);
    var name = clean(row && row.label, 80) || (isChinese() ? '朋友' : 'friend');

    if (mode !== 'family' && !station) {
      return {
        title: 'LittleG',
        text: isChinese() ? '先為好友選擇港鐵站。' : 'Choose a station for this friend first.',
        url: safeShareUrl(),
      };
    }

    if (mode === 'meetup')
      return {
        title: isChinese() ? 'LittleG 約見面' : 'LittleG Meet up',
        text: isChinese()
          ? '☕ ' + name + '，有空見面嗎？不如在 ' + station + ' 港鐵站附近見。'
          : '☕ ' + name + ', free to meet? Let’s meet near ' + station + ' MTR Station.',
        url: safeShareUrl(),
      };

    if (mode === 'hello')
      return {
        title: isChinese() ? 'LittleG 打招呼' : 'LittleG Say hi',
        text: isChinese()
          ? '👋 ' + name + '，你好！有空在 ' + station + ' 港鐵站附近見面嗎？'
          : '👋 Hi ' + name + '! Free to meet near ' + station + ' MTR Station?',
        url: safeShareUrl(),
      };

    return {
      title: 'LittleG Friends Map',
      text: isChinese()
        ? '一起用 LittleG 朋友圖。只分享安全連結，不包含住址或座標。'
        : 'Join me on LittleG Friends Map. This safe link contains no home address or coordinates.',
      url: safeShareUrl(),
    };
  }

  function encodedShareText(payload) {
    return encodeURIComponent(clean(payload.text, 500) + ' ' + payload.url);
  }

  function providerUrl(provider, payload) {
    var textVal = encodedShareText(payload);
    if (provider === 'whatsapp') return 'https://wa.me/?text=' + textVal;
    if (provider === 'messages') return 'sms:?&body=' + textVal;
    if (provider === 'email')
      return 'mailto:?subject=' + encodeURIComponent(payload.title) + '&body=' + textVal;
    return '';
  }

  function ensureShareSheet(root) {
    var existing = q(root, '[data-kid-share-sheet]');
    if (existing) return existing;

    var sheet = document.createElement('section');
    sheet.className = 'kid-gmaps-sheet kid-gmaps-share-sheet';
    sheet.setAttribute('data-kid-gmaps-sheet', '');
    sheet.setAttribute('data-kid-share-sheet', '');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-labelledby', 'KidGmapsShareSheetTitle');
    sheet.tabIndex = -1;
    sheet.hidden = true;
    sheet.innerHTML =
      '<div class="kid-gmaps-sheet__body">' +
      sheetHeader(isChinese() ? '💬 分享' : '💬 Share', 'data-kid-language-sheet-close') +
      '<p data-kid-share-message></p>' +
      '<div class="kid-gmaps-share-provider-grid" data-kid-share-providers>' +
      '<a class="kid-map-action kid-map-action--primary" data-kid-share-provider="whatsapp" target="_blank" rel="noopener">WhatsApp</a>' +
      '<a class="kid-map-action" data-kid-share-provider="messages">Messages</a>' +
      '<a class="kid-map-action" data-kid-share-provider="email">Email</a>' +
      '<button type="button" class="kid-map-action" data-kid-share-wechat>WeChat</button>' +
      '<button type="button" class="kid-map-action" data-kid-share-copy>' +
      (isChinese() ? '複製' : 'Copy') +
      '</button>' +
      '<button type="button" class="kid-map-action" data-kid-share-native>' +
      (isChinese() ? '更多…' : 'More…') +
      '</button>' +
      '</div>' +
      '<div class="kid-gmaps-share-qr" data-kid-share-qr aria-label="QR code"></div>' +
      '<p class="kid-gmaps-share-privacy">' +
      (isChinese()
        ? '🔒 安全連結不包含住址、車站 ID 或座標。'
        : '🔒 The safe link contains no home address, station ID, or coordinates.') +
      '</p>' +
      '</div>';

    q(sheet, 'h2').id = 'KidGmapsShareSheetTitle';
    root.appendChild(sheet);

    prepareSheet(sheet, function () {
      hideSheet(sheet);
    });
    q(sheet, '[data-kid-language-sheet-close]').addEventListener('click', function () {
      hideSheet(sheet);
    });

    q(sheet, '[data-kid-share-native]').addEventListener('click', function () {
      var payload = sheet.__kidSharePayload || shareMessage(root, 'family');
      sharePayload(payload, payload.text + ' ' + payload.url, root);
    });

    q(sheet, '[data-kid-share-copy]').addEventListener('click', function () {
      var payload = sheet.__kidSharePayload || shareMessage(root, 'family');
      copyText(payload.text + ' ' + payload.url, root, isChinese() ? '內容已複製。' : 'Copied.');
    });

    q(sheet, '[data-kid-share-wechat]').addEventListener('click', function () {
      var payload = sheet.__kidSharePayload || shareMessage(root, 'family');
      copyText(
        payload.text + ' ' + payload.url,
        root,
        isChinese()
          ? '已複製。可貼到 WeChat，或讓對方掃 QR。'
          : 'Copied. Paste in WeChat or scan the QR code.'
      );
    });
    return sheet;
  }

  function renderShareQr(root, sheet) {
    var host = q(sheet, '[data-kid-share-qr]');
    if (!host) return;

    function fallback() {
      host.replaceChildren();
      var node = document.createElement('p');
      node.className = 'kid-gmaps-share-qr__fallback';
      node.textContent = isChinese()
        ? 'QR 暫時未能顯示，仍可使用分享或複製。'
        : 'QR is unavailable. You can still share or copy.';
      host.appendChild(node);
    }

    host.replaceChildren();
    if (typeof window.QRCode !== 'function') {
      fallback();
      return;
    }
    try {
      new window.QRCode(host, { text: safeShareUrl(), width: 220, height: 220 });
    } catch (_) {
      fallback();
    }
  }

  function openShareSheet(root, options) {
    var sheet = ensureShareSheet(root);
    var mode = options && options.mode ? options.mode : 'family';
    var payload = shareMessage(root, mode);

    if (mode !== 'family' && !stationName(currentSelectedRow(root))) {
      toast(root, payload.text);
      return;
    }

    sheet.__kidSharePayload = payload;
    var heading = q(sheet, '.kid-add-modal__head h2');
    if (heading) {
      heading.textContent =
        mode === 'meetup'
          ? isChinese()
            ? '☕ 約見面'
            : '☕ Meet up'
          : mode === 'hello'
            ? isChinese()
              ? '👋 打招呼'
              : '👋 Say hi'
            : isChinese()
              ? '💬 傳給家人'
              : '💬 Share with family';
    }

    var message = q(sheet, '[data-kid-share-message]');
    if (message) message.textContent = payload.text;

    qa(sheet, '[data-kid-share-provider]').forEach(function (node) {
      var provider = node.getAttribute('data-kid-share-provider');
      var href = providerUrl(provider, payload);
      if (href) node.setAttribute('href', href);
      else node.removeAttribute('href');
    });

    closeTransientSheets(root, sheet);
    showSheet(sheet, options && options.trigger);
    renderShareQr(root, sheet);
  }

  function bindStationModalBridge(root) {
    if (root.__kidStationBridgeBound) return;
    root.__kidStationBridgeBound = true;

    var add = q(root, '[data-kid-map-add-modal]');
    var stationHost = promoteStationDialog(root);
    var picker = q(root, '[data-kid-station-picker]');

    if (!add || !stationHost || !picker) return;

    function closeStationTopLayer() {
      if (
        stationHost.tagName === 'DIALOG' &&
        stationHost.open &&
        typeof stationHost.close === 'function'
      ) {
        try {
          stationHost.close();
        } catch (_) {}
      }
      stationHost.hidden = true;
      stationHost.setAttribute('aria-hidden', 'true');
    }

    function reopenAdd() {
      if (!root.__kidReturnToAddAfterStation) return;
      root.__kidReturnToAddAfterStation = false;
      closeStationTopLayer();

      window.setTimeout(function () {
        if (!add.open) {
          try {
            if (typeof add.showModal === 'function') {
              add.showModal();
            } else {
              add.setAttribute('open', '');
            }
          } catch (_) {
            add.setAttribute('open', '');
          }
        }

        function focusReturnedPicker(attempt) {
          if (!add.open || !visibleFocusTarget(picker)) {
            if (attempt < 6) {
              window.requestAnimationFrame(function () {
                focusReturnedPicker(attempt + 1);
              });
            }
            return;
          }

          try {
            picker.focus({ preventScroll: true });
          } catch (_) {
            picker.focus();
          }

          if (document.activeElement !== picker && attempt < 6) {
            window.requestAnimationFrame(function () {
              focusReturnedPicker(attempt + 1);
            });
          }
        }

        window.requestAnimationFrame(function () {
          focusReturnedPicker(0);
        });
      }, 0);
    }

    function showStationWhenReady(attempt) {
      if (stationHost.hidden) {
        if ((attempt || 0) < 100) {
          window.setTimeout(function () {
            showStationWhenReady((attempt || 0) + 1);
          }, 25);
          return;
        }
        reopenAdd();
        toast(
          root,
          isChinese() ? '車站資料暫時未能載入。' : 'Station data is temporarily unavailable.'
        );
        return;
      }
      return;
    }

    picker.addEventListener(
      'click',
      function () {
        root.__kidReturnToAddAfterStation = !!add.open;
        if (add.open && typeof add.close === 'function') {
          try {
            add.close();
          } catch (_) {
            add.removeAttribute('open');
          }
        } else add.removeAttribute('open');
        window.setTimeout(function () {
          showStationWhenReady(0);
        }, 0);
      },
      true
    );

    stationHost.addEventListener('click', function (event) {
      if (event.target.closest('[data-kid-station-result], [data-kid-station-close]'))
        window.setTimeout(reopenAdd, 20);
    });

    stationHost.addEventListener('cancel', function (event) {
      event.preventDefault();
      var close = q(stationHost, '[data-kid-station-close]');
      if (close) close.click();
      else reopenAdd();
    });
  }

  function syncSearchZeroState(root) {
    if (!(root instanceof HTMLElement)) return;

    var slot = q(root, '[data-kid-map-search-slot]');
    var input = q(slot, '[data-gproperty-map-search-input]');
    var suggestions = q(slot, '[data-gproperty-map-suggestions]');
    var zero = q(slot, '[data-kid-search-zero-state]');
    if (!input) return;

    var active = root.getAttribute('data-kid-search-engaged') === 'true';
    var queryEmpty = !clean(input.value, 160);
    var tray = q(slot, '[data-kid-search-shortcuts]');
    if (tray) {
      var showShortcuts = active && queryEmpty && root.__kidExplicitStationSearch !== true;
      tray.hidden = !showShortcuts;
      tray.setAttribute('aria-hidden', showShortcuts ? 'false' : 'true');
    }
    if (!zero) return;

    var hasSuggestions = Boolean(suggestions && !suggestions.hidden && suggestions.children.length);
    var show = active && queryEmpty && !hasSuggestions;

    zero.hidden = !show;
    zero.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  function ensureSearchZeroState(root, attempt) {
    var slot = q(root, '[data-kid-map-search-slot]');
    var input = q(slot, '[data-gproperty-map-search-input]');
    var suggestions = q(slot, '[data-gproperty-map-suggestions]');

    if (!slot || !input || !suggestions) {
      if ((attempt || 0) < 80) {
        window.setTimeout(function () {
          ensureSearchZeroState(root, (attempt || 0) + 1);
        }, 75);
      }
      return;
    }

    var zero = q(slot, '[data-kid-search-zero-state]');

    if (!zero) {
      zero = document.createElement('div');
      zero.className = 'kid-map__search-zero-state visually-hidden';
      zero.setAttribute('data-kid-search-zero-state', '');
      zero.setAttribute('role', 'status');
      zero.setAttribute('aria-live', 'polite');
      zero.setAttribute('aria-atomic', 'true');
      zero.innerHTML =
        '<span aria-hidden="true">👀</span><span>' +
        (isSimplifiedChinese()
          ? '搜索朋友、地点或港铁站'
          : isChinese()
            ? '搜尋朋友、地點或港鐵站'
            : 'Find friends, places, or MTR stations') +
        '</span>';

      zero.hidden = true;
      slot.appendChild(zero);
    }

    if (!input.__kidZeroStateBound) {
      input.__kidZeroStateBound = true;
      input.addEventListener('input', function () {
        syncSearchZeroState(root);
      });
      input.addEventListener('focus', function () {
        window.setTimeout(function () {
          syncSearchZeroState(root);
        }, 0);
      });
    }

    if (!suggestions.__kidZeroStateObserver && typeof MutationObserver === 'function') {
      suggestions.__kidZeroStateObserver = new MutationObserver(function () {
        syncSearchZeroState(root);
      });
      suggestions.__kidZeroStateObserver.observe(suggestions, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden'],
      });
    }

    syncSearchZeroState(root);
  }

  function setSearchEngaged(root, engaged) {
    if (!(root instanceof HTMLElement)) return;
    var active = engaged === true;
    root.setAttribute('data-kid-search-engaged', active ? 'true' : 'false');
    var tray = q(root, '[data-kid-search-shortcuts]');
    if (tray) {
      tray.hidden = !active;
      tray.setAttribute('aria-hidden', active ? 'false' : 'true');
    }
    syncSearchZeroState(root);
  }

  function collapseSearchChrome(root) {
    if (!(root instanceof HTMLElement)) return;
    root.__kidExplicitStationSearch = false;
    root.__kidSearchSuggestionsSettled = true;
    setSearchEngaged(root, false);

    var slot = q(root, '[data-kid-map-search-slot]');
    var input = q(slot, '[data-gproperty-map-search-input]');
    var suggestions = q(slot, '[data-gproperty-map-suggestions]');

    if (suggestions) suggestions.hidden = true;

    if (input) {
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-activedescendant', '');
      if (document.activeElement === input && typeof input.blur === 'function') {
        input.blur();
      }
    }
  }

  function bindSearchEngagement(root) {
    if (!root || root.__kidSearchEngagementBound) return;
    var slot = q(root, '[data-kid-map-search-slot]');
    var searchRoot = q(slot, '[data-kid-map-search-root], [data-gproperty-map-search-root]');
    var input = q(slot, '[data-gproperty-map-search-input]');
    if (!slot || !searchRoot || !input) return;

    root.__kidSearchEngagementBound = true;
    setSearchEngaged(root, false);

    searchRoot.addEventListener('pointerdown', function () {
      setSearchEngaged(root, true);
    });
    searchRoot.addEventListener('focusin', function () {
      setSearchEngaged(root, true);
    });

    slot.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (!slot.contains(document.activeElement)) collapseSearchChrome(root);
      }, 0);
    });
  }

  function ensureSearchShortcuts(root, attempt) {
    var slot = q(root, '[data-kid-map-search-slot]');
    var searchRoot = q(slot, '[data-kid-map-search-root], [data-gproperty-map-search-root]');
    if (!slot || !searchRoot || searchRoot.parentNode !== slot) {
      if ((attempt || 0) < 80)
        window.setTimeout(function () {
          ensureSearchShortcuts(root, (attempt || 0) + 1);
        }, 75);
      return;
    }
    if (q(slot, '[data-kid-search-shortcuts]')) return;

    var tray = document.createElement('div');
    tray.className = 'kid-map__search-shortcuts';
    tray.setAttribute('data-kid-search-shortcuts', '');
    tray.setAttribute('role', 'group');
    tray.setAttribute(
      'aria-label',
      isSimplifiedChinese()
        ? '快速搜索与筛选'
        : isChinese()
          ? '快速搜尋與篩選'
          : 'Quick search and filters'
    );
    tray.setAttribute('aria-hidden', 'true');
    tray.hidden = true;

    [
      { key: 'friends', icon: '👥', label: isChinese() ? '朋友' : 'Friends' },
      { key: 'places', icon: '🏠', label: isChinese() ? '地點' : 'Places' },
      {
        key: 'mtr',
        icon: '🚉',
        label: isSimplifiedChinese() ? '港铁' : isChinese() ? '港鐵' : 'MTR',
      },
    ].forEach(function (item) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'kid-map__search-shortcut';
      button.setAttribute('data-kid-search-shortcut', item.key);
      button.setAttribute('aria-label', item.label);
      button.innerHTML =
        '<span aria-hidden="true">' +
        item.icon +
        '</span><span>' +
        escapeHtml(item.label) +
        '</span>';
      tray.appendChild(button);
    });

    tray.addEventListener('click', function (event) {
      var button = event.target.closest('[data-kid-search-shortcut]');
      if (!button) return;
      var key = button.getAttribute('data-kid-search-shortcut');
      var selectedSocialId = clean(root.getAttribute('data-kid-selected-social-id'), 120);
      var activeFilter = clean(root.getAttribute('data-kid-active-filter'), 30);

      if (key === 'friends') {
        if (selectedSocialId && activeFilter === 'friends') {
          renderSocialSheet(root, 'friend', null);
        } else {
          q(root, '[data-kid-filter="friends"]')?.click();
        }
      }

      if (key === 'places') {
        if (selectedSocialId && activeFilter === 'mine') {
          renderSocialSheet(root, 'mine', null);
        } else {
          q(root, '[data-kid-filter="mine"]')?.click();
        }
      }

      if (key === 'mtr') {
        closeTransientSheets(root, null);
        collapseSearchChrome(root);
        var mapRuntime = window.LittleGKidMap;
        if (mapRuntime && typeof mapRuntime.showGlobalStations === 'function') {
          mapRuntime.showGlobalStations();
        } else {
          var input = q(searchRoot, '[data-gproperty-map-search-input]');
          if (input && typeof input.focus === 'function') input.focus();
        }
        return;
      }

      collapseSearchChrome(root);
    });

    slot.appendChild(tray);
    bindSearchEngagement(root);
  }

  function projectMapControlComposition(root) {
    if (!(root instanceof HTMLElement)) return;
    var stack = q(root, '[data-floating-map-controls]');
    if (!stack) return;

    var zoomIn = q(root, '[data-kid-zoom-in]');
    var zoomOut = q(root, '[data-kid-zoom-out]');
    var compass = q(root, '[data-kid-map-compass]');
    var locate = q(root, '[data-gproperty-map-use-location]');
    var passport = q(root, '[data-kid-passport-link]');

    [zoomIn, zoomOut, compass, locate, passport].forEach(function (node) {
      if (node) stack.appendChild(node);
    });

    root.setAttribute('data-kid-control-projection', 'plus-minus-compass-locate-passport');
  }

  function bindKeyboardMapControls(root) {
    if (!root || root.__kidKeyboardMapControlsObserver || typeof MutationObserver !== 'function')
      return;
    var controls = q(root, '[data-floating-map-controls]');
    if (!controls) return;

    function sync() {
      var hidden = root.getAttribute('data-keyboard-open') === 'true';
      controls.inert = hidden;
      if (hidden) controls.setAttribute('aria-hidden', 'true');
      else controls.removeAttribute('aria-hidden');
    }

    var observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['data-keyboard-open'] });
    root.__kidKeyboardMapControlsObserver = observer;
    sync();
  }

  function bindCompassControl(root) {
    var button = q(root, '[data-kid-map-compass]');
    if (!button || button.__kidCompassBound) return;
    button.__kidCompassBound = true;

    button.addEventListener('click', function () {
      var renderer = window.LittleGKidMapRenderer;
      var map = renderer && typeof renderer.mapFor === 'function' ? renderer.mapFor(root) : null;
      if (!map) return;

      var reduceMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (typeof map.easeTo === 'function') {
        map.easeTo({
          bearing: 0,
          pitch: 0,
          duration: reduceMotion ? 0 : 260,
        });
        return;
      }

      if (typeof map.setBearing === 'function') map.setBearing(0);
      if (typeof map.setPitch === 'function') map.setPitch(0);
    });
  }

  function bindMapSurfaceState(root) {
    if (!root || root.__kidGmapsBlankMapBound) return;
    root.__kidGmapsBlankMapBound = true;

    root.addEventListener('gproperty:map:blank-click', function () {
      closeTransientSheets(root, null);
      collapseSearchChrome(root);
    });
  }

  function observeSelection(root) {
    if (root.__kidGmapsSelectionObserver || typeof MutationObserver !== 'function') return;
    var observer = new MutationObserver(function (records) {
      var selectedChanged = records.some(function (record) {
        return (
          record.type === 'attributes' && record.attributeName === 'data-kid-selected-social-id'
        );
      });
      if (!selectedChanged) return;
      collapseSearchChrome(root);
      setFriendActionsExpanded(root, false);
      var row = currentSelectedRow(root);
      if (row) flyToRow(root, row);
      ensureFriendActions(root);
      syncFriendActions(root);
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-kid-selected-social-id'] });
    root.__kidGmapsSelectionObserver = observer;
  }

  function bindGraphRefresh(root) {
    if (root.__kidGmapsGraphBound) return;
    root.__kidGmapsGraphBound = true;

    document.addEventListener('littleg:family-social-graph-change', function () {
      syncOpeningState(root);
      var sheet = q(root, '[data-kid-social-sheet]');
      if (sheet && !sheet.hidden) {
        var kind = root.getAttribute('data-kid-active-filter') === 'mine' ? 'mine' : 'friend';
        renderSocialSheet(
          root,
          kind,
          sheet.__kidGmapsOpener ||
            q(root, kind === 'mine' ? '[data-kid-filter="mine"]' : '[data-kid-filter="friends"]'),
          sheet.__kidStationFilterId ? { stationId: sheet.__kidStationFilterId } : null
        );
      }
      syncFriendActions(root);
    });
  }

  /* 優先級 Escape 鍵逐層解鎖調度器 (Priority Escape Dismissal) */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    var socialSheet = document.querySelector(
      '.littleg-kid-map [data-kid-social-sheet]:not([hidden])'
    );
    if (socialSheet) {
      event.stopPropagation();
      hideSheet(socialSheet);
      return;
    }

    var shareSheet = document.querySelector(
      '.littleg-kid-map [data-kid-share-sheet]:not([hidden])'
    );
    if (shareSheet) {
      event.stopPropagation();
      hideSheet(shareSheet);
      return;
    }

    var languageSheet = document.querySelector(
      '.littleg-kid-map [data-kid-language-sheet]:not([hidden])'
    );
    if (languageSheet) {
      event.stopPropagation();
      hideSheet(languageSheet);
      return;
    }

    var root = document.querySelector(ROOT_SELECTOR);
    var slot = root ? q(root, '[data-kid-map-search-slot]') : null;
    var suggestions = slot ? q(slot, '[data-gproperty-map-suggestions]') : null;

    if (
      root &&
      (root.getAttribute('data-kid-search-engaged') === 'true' ||
        root.__kidExplicitStationSearch === true ||
        (suggestions && !suggestions.hidden))
    ) {
      event.stopPropagation();
      collapseSearchChrome(root);
    }
  });

  function mountRoot(root) {
    if (!root || root.__kidGmapsStandardVersion === VERSION) return;
    root.__kidGmapsStandardVersion = VERSION;
    root.setAttribute('data-kid-interaction-standard', 'google-maps-familiar-v1');

    promoteStationDialog(root);
    prepareExistingSheets(root);
    ensureSocialSheet(root);
    ensureShareSheet(root);
    decorateCoreFilters(root);
    bindStationFriendsSheet(root);
    setupLanguageSheet(root);
    bindMemoryViewSelector(root);
    bindMotionPreference(root);
    bindNightModePreference(root);
    ensureOfflineBanner(root);
    bindOpeningState(root);
    ensureFriendActions(root);
    ensureSearchShortcuts(root, 0);
    ensureSearchZeroState(root, 0);
    bindSearchEngagement(root);
    projectMapControlComposition(root);
    bindKeyboardMapControls(root);
    bindCompassControl(root);
    bindStationModalBridge(root);
    observeSelection(root);
    bindGraphRefresh(root);
    bindMapSurfaceState(root);
  }

  function mountAll() {
    qa(document, ROOT_SELECTOR).forEach(mountRoot);
  }

  // Shopify Section Unload 生命週期釋放與記憶體解綁 (Memory Leak Prevention)
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target ? event.target.querySelector(ROOT_SELECTOR) : null;
    if (!(root instanceof HTMLElement)) return;

    if (root.__kidGmapsSelectionObserver) root.__kidGmapsSelectionObserver.disconnect();
    if (root.__kidKeyboardMapControlsObserver) {
      root.__kidKeyboardMapControlsObserver.disconnect();
      root.__kidKeyboardMapControlsObserver = null;
    }
    var controls = q(root, '[data-floating-map-controls]');
    if (controls) {
      controls.inert = false;
      controls.removeAttribute('aria-hidden');
    }
    var slot = q(root, '[data-kid-map-search-slot]');
    var suggestions = slot ? q(slot, '[data-gproperty-map-suggestions]') : null;
    if (suggestions && suggestions.__kidZeroStateObserver) {
      suggestions.__kidZeroStateObserver.disconnect();
    }

    root.__kidGmapsStandardVersion = null;
    lastActiveSheetElement = null;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', mountAll);

  window.LittleGKidInteractionStandard = Object.freeze({
    version: VERSION,
    mount: mountAll,
    interactionStandard: 'google-maps-familiar-v1',
    socialGraphOwner: 'LittleGFamilyData.socialGraph',
    mapOwner: 'assets/gcitymap-platform.js',
    locationPersistence: false,
    realtimeLocation: false,
    semanticFriendCardOwner: false,
    nearbyOwner: false,
    stationDataOwner: false,
    shareProviders: ['whatsapp', 'messages', 'email', 'wechat', 'copy', 'more'],
    presentationActions: ['directions', 'meetup', 'say-hi', 'share'],
    mapControlProjection: 'plus-minus-compass-locate-passport',
    visibleMapControls: ['plus', 'minus', 'compass', 'locate', 'passport'],
    mobileVisibleMapControls: ['plus', 'minus', 'compass', 'locate', 'passport'],
    headerActions: ['language', 'constellation'],
    socialControlSurface: 'search-shortcuts',
    searchShortcutsVisibility: 'engaged-only',
  });
})(window, document);
